import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CardModule } from 'primeng/card';
import { IParty } from '@harlowe/core';
import { FormsModule } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { NgFor, NgIf } from '@angular/common';
import { Store } from '@ngxs/store';
import { ReferralState } from '../../../state/referrals/referral-state';
import { TealCompReferralSubmission } from '../../../models/teal-comp-referral';
import { TableModule } from 'primeng/table';
import { AddressInsert, AddressUpdate, IAddress } from '../../../models/address';
import {ILocation } from '@harlowe/core';
import { PanelModule } from 'primeng/panel';
import { CheckboxModule } from 'primeng/checkbox';
import { PartyService } from '@harlowe/core';
import { IPhone, PhoneInsert, PhoneUpdate } from '../../../models/phone';
import { EmailInsert, EmailUpdate, IEmail } from '@harlowe/core';
import { InputMaskModule } from 'primeng/inputmask';
import { TooltipModule } from 'primeng/tooltip';


@Component({
  selector: 'app-party-editor',
  standalone: true,
  imports: [CardModule,FormsModule,FloatLabelModule,InputTextModule,
            CalendarModule,ButtonModule,ButtonGroupModule, NgIf,TableModule,
            NgFor, PanelModule, CheckboxModule, InputMaskModule, TooltipModule],
  templateUrl: './party-editor.component.html',
  styleUrl: './party-editor.component.scss'
})
export class PartyEditorComponent 
{
  @Input() party:IParty = {} as IParty;
  @Output() saved: EventEmitter<IParty> = new EventEmitter<IParty>();
  @Output() cancelled = new EventEmitter();

  //Listen for whether the user has selected a referral to work with
  selectedReferral$ = this._store.select(ReferralState.currentReferral);
  activeSubmission?: TealCompReferralSubmission;
  editedAddresses: number[] = [];
  editedEmails: number [] = [];
  editedPhones: number [] = [];

  constructor(private _store:Store, private _partyService:PartyService)
  {
    this.selectedReferral$.subscribe
    (
      referral =>
        {
          this.activeSubmission = referral?.submission;
        }
    )
  }

  saveButtonPressed(event: MouseEvent)
  {
    this.saved.emit(this.party);
  }

  cancelButtonPressed(event: MouseEvent)
  {
    this.cancelled.emit();
  }

  allowSave() : boolean
  {
    return this.party?.firstName != null && this.party.firstName != "" && this.party.lastName != null && this.party.lastName != ""  && this.party.dateOfBirth != null 
          &&this.party.addresses.length > 0 && this.party.phones.length > 0;
  }

  fieldsMissing() : string
  {
    var baseString = "";
    baseString += this.party?.firstName == null || this.party.firstName == "" ? "Patient First Name, " : "";
    baseString += this.party?.lastName == null || this.party.lastName == "" ? "Patient Last Name, " : "";
    baseString += this.party?.dateOfBirth == null ? "Patient Date Of Birth, " : "";
    baseString += this.party?.addresses?.length ?? 0 == 0 ? "Patient Address, " : "";
    baseString += this.party?.phones?.length ?? 0 == 0 ? "Patient Phone, " : "";
    baseString = baseString.replace(new RegExp( ', $'), '');
    if(baseString != "")
      baseString = "Missing: " + baseString;
    return baseString;
  }

  fillFromSubmission()
  {
    if(this.activeSubmission)
    {

      this.party.firstName = this.activeSubmission.patientFirstName;
      this.party.lastName = this.activeSubmission.patientLastName;
      this.party.dateOfBirth = new Date(this.activeSubmission.patientDateOfBirth);

      //Address, Phone and Email
      var newAddress =this.addBlankAddress(undefined);
      newAddress.location.street1 = this.activeSubmission.addressLine1;
      newAddress.location.street2 = this.activeSubmission.addressLine2;
      newAddress.location.city = this.activeSubmission.addressCity;
      newAddress.location.state = this.activeSubmission.addressState;
      newAddress.location.zipCode = this.activeSubmission.addressZip;
      newAddress.addressType = 0;
      this.markAddressAsEdited(newAddress);


      if(this.activeSubmission.patientEmail != null)
      {
        var newEmail = this.addBlankEmail(undefined);
        newEmail.emailAddress = this.activeSubmission.patientEmail;
        newEmail.emailType = 0;
        this.markEmailAsEdited(newEmail);
      }
      var newPhone = this.addBlankPhone(undefined);
      newPhone.phoneNumber = this.activeSubmission.patientPhone;
      newPhone.phoneType = 0;
      this.markPhoneAsEdited(newPhone);

    }
  }

  clearForm()
  {
    this.party = {} as IParty;
  }

  addBlankAddress(event?: MouseEvent): IAddress
  {
    var minId = (this.party.addresses && this.party.addresses.length > 0) ? Math.min(... this.party.addresses.map(a => a.id),0) : 0;

    var newLocation = {} as ILocation;
    var newAddress = {location:newLocation,addressType:1,id:minId - 1} as IAddress;
    this.party.addresses = [...this.party.addresses ?? [], newAddress];
    return newAddress;
  }

  addBlankPhone(event?:MouseEvent) : IPhone
  {
    var minId = (this.party.phones && this.party.phones.length > 0) ? Math.min(... this.party.phones.map(p => p.id),0) : 0;

    var newPhone = {phoneType:1,id:minId-1} as IPhone;
    this.party.phones = [...this.party.phones ?? [], newPhone];
    return newPhone;
  }

  addBlankEmail(event?:MouseEvent) : IEmail
  {
    var minId = (this.party.emails && this.party.emails.length > 0) ? Math.min(... this.party.emails.map(e => e.id),0) : 0;

    var newEmail = {emailType:1,id:minId-1} as IEmail;
    this.party.emails = [...this.party.emails ?? [], newEmail];
    return newEmail;
  }

  async makeAddressPrimary(address:IAddress)
  {
    //Make this primary
    address.addressType = 0;
    await this.saveAddress(address);

    //Make all other addresses have an 'other' address type
    var pastPrimary = this.party.addresses.find(a => a.addressType == 0 && a.id != address.id);
    if(pastPrimary)
    {
      pastPrimary.addressType = 1;
      await this.saveAddress(pastPrimary)
    }

  }

  async deleteAddress(address:IAddress)
  {
    if(address.id < 0)
    {
      this.party.addresses = [...this.party.addresses.filter(a => a.id != address.id)];  
    }
    else
    {
      //delete addres 
      var addr = await this._partyService.deleteAddress(this.party.id,address.id);

      this.party.addresses = [...this.party.addresses.filter(a => a.id != addr.id)];

      //If this was a primary address, also make the first address in the list primary, and update that
      if(addr.addressType == 0)
        {
          var newPrimary = this.party.addresses.find(a => a.id != addr.id);
          if(newPrimary)
            {
              newPrimary.addressType = 0;
              await this.saveAddress(newPrimary);
            }
        }
    }
  }

  async saveAddress(address:IAddress)
  {
    if(address.id < 0)
    {
      var ai = new AddressInsert(address);
      //If this is the first address, make it primary
      if(this.party.addresses.filter(a => a.id > 0 && a.addressType == 0).length == 0 )
        ai.addressType = 0;

      var newAddress= await this._partyService.addAddress(this.party.id,ai);
      if(newAddress)
        this.party.addresses = [...this.party.addresses.filter(aa => aa.id != address.id),newAddress];
    }
    else
    {
      //update
      var au = new AddressUpdate(address.id,address.addressType,address.location.street1,address.location.street2,address.location.city,address.location.state,address.location.zipCode)
      var aa = await this._partyService.updateAddress(this.party.id,address.id,au);
      this.party.addresses[this.party.addresses.findIndex(a => a.id == aa.id)] = aa;

    }

    this.editedAddresses = [...this.editedAddresses.filter(aa => aa != address.id)];
  }

  markAddressAsEdited(address: IAddress)
  {
    this.editedAddresses = [...this.editedAddresses.filter(v => v != address.id), address.id];
  }


  async makeEmailPrimary(email:IEmail)
  {
    //Make this primary
    email.emailType = 0;
    await this.saveEmail(email);

    //Make all other addresses have an 'other' address type
    var pastPrimary = this.party.emails.find(e => e.emailType == 0 && e.id != email.id);
    if(pastPrimary)
    {
      pastPrimary.emailType = 1;
      await this.saveEmail(pastPrimary)
    }

  }

  async deleteEmail(email:IEmail)
  {
    if(email.id < 0)
    {
      this.party.emails = [...this.party.emails.filter(e => e.id != email.id)];  
    }
    else
    {
      //delete email 
      var em = await this._partyService.deleteEmail(this.party.id,email.id);


      this.party.emails = [...this.party.emails.filter(e => e.id != em.id)];

      //If this was a primary address, also make the first address in the list primary, and update that
      if(em.emailType == 0)
        {
          var newPrimary = this.party.emails.find(a => a.id != em.id);
          if(newPrimary)
            {
              newPrimary.emailType = 0;
              await this.saveEmail(newPrimary);
            }
        }
    }
  }

  async saveEmail(email:IEmail)
  {
    if(email.id < 0)
    {
      var ei = new EmailInsert(email);
      //If this is the first address, make it primary
      if(this.party.emails.filter(e => e.id > 0 && e.emailType == 0).length == 0 )
        ei.emailType = 0;

      
      this.party.emails = [...this.party.emails.filter(ee => ee.id !=email.id),await this._partyService.addEmail(this.party.id,ei)];
    }
    else
    {
      //update
      var eu = new EmailUpdate(email.id,email.emailAddress,email.emailType);
      var ee = await this._partyService.updateEmail(this.party.id,email.id,eu);
      this.party.emails[this.party.emails.findIndex(e => e.id == ee.id)] = ee;
    }

    this.editedEmails = [...this.editedEmails.filter(ee => ee != email.id)];
  }

  markEmailAsEdited(email: IEmail)
  {
    this.editedEmails = [...this.editedEmails.filter(v => v != email.id), email.id];

  }


  async makePhonePrimary(phone:IPhone)
  {
    //Make this primary
    phone.phoneType = 0;
    await this.savePhone(phone);

    //Make all other addresses have an 'other' address type
    var pastPrimary = this.party.phones.find(p => p.phoneType == 0 && p.id != phone.id);
    if(pastPrimary)
    {
      pastPrimary.phoneType = 1;
      await this.savePhone(pastPrimary)
    }

  }

  async deletePhone(phone:IPhone)
  {
    if(phone.id < 0)
    {
      this.party.phones = [...this.party.phones.filter(p => p.id != phone.id)];  
    }
    else
    {
      //delete email 
      var p = await this._partyService.deletePhone(this.party.id,phone.id);
      this.party.phones = [...this.party.phones.filter(ph => ph.id != p.id)];

      //If this was a primary address, also make the first address in the list primary, and update that
      if(p.phoneType == 0)
        {
          var newPrimary = this.party.phones.find(a => a.id != p.id);
          if(newPrimary)
            {
              newPrimary.phoneType = 0;
              await this.savePhone(newPrimary);
            }
        }
    }
  }

  async savePhone(phone:IPhone)
  {

    //The phone mask input control only works with US numbers. The first 10 digits are area code, exchange and line number
    //Any additional numbers are extension fields
    var numsOnly=phone.phoneNumber.replace(/\D/g, "").trim();
    var phoneNumber = numsOnly.substring(0,10);
    var extension = numsOnly.length > 10 ? numsOnly.substring(11,numsOnly.length) : undefined;

    if(phone.id < 0)
    {


      var pi = new PhoneInsert(phone);
      //If this is the first address, make it primary
      if(this.party.phones.filter(p => p.id > 0 && p.phoneType == 0).length == 0 )
        pi.phoneType = 0;

      
      this.party.phones = [...this.party.phones.filter(pp => pp.id != phone.id), await this._partyService.addPhone(this.party.id,pi)];
    }
    else
    {
      //update
      var pu = new PhoneUpdate(phone.id,phone.phoneType,"001",phoneNumber,phone.smsCapable,extension,extension?false:true);
      var pp = await this._partyService.updatePhone(this.party.id,phone.id,pu);
      this.party.phones[this.party.phones.findIndex(p => p.id == pp.id)] = pp;
    }
    
    this.editedPhones = [...this.editedPhones.filter(pp => pp != phone.id)];
  }

  markPhoneAsEdited(phone: IPhone)
  {
    this.editedPhones = [...this.editedPhones.filter(v => v != phone.id), phone.id];
  }


}
